import { ITelemetryItem } from "@microsoft/applicationinsights-web";
import { CONSOLE_LEVEL_KEY, LogLevel, getLocalStorageLogLevel } from "@pcp/logging";

/**
 * Prints each telemetry item as it is finally composed, so what was sent can be checked without
 * querying Application Insights.
 *
 * ⚠️ This answers "what did the SDK accept", not "what left the browser". A content blocker or a
 * failed request drops items *after* this point and the echo still prints them — which is exactly
 * how it separates "never emitted" from "emitted but not delivered", the failure mode a developer
 * running an ad blocker otherwise mistakes for broken instrumentation. For the second question a
 * notification listener on `eventsSent` would be the right instrument.
 *
 * It never sees items {@link shouldDropTelemetry} rejected, because an initializer returning
 * `false` ends the chain. That is intended: those are known noise by definition.
 */
export function echoTelemetryToConsole(item: ITelemetryItem): void {
    if (!isEchoEnabled()) {
        return;
    }

    // Printed one microtask late, and read only then, so the line shows the FULLY enriched item.
    // Registering this initializer last within the factory is not enough: the lesson-meta
    // initializers are registered later still, at app init, and they are the ones attaching
    // presentation context — the enrichment most worth eyeballing. Initializers run synchronously
    // in registration order over one shared envelope object, and those two *reassign*
    // `envelope.data`, so deferring the read past the end of the chain picks up their work through
    // the reference we already hold. Reading `item` here instead would print a pre-enrichment view.
    queueMicrotask(() => {
        // eslint-disable-next-line no-console
        console.debug(`[telemetry] ${item.baseType ?? 'unknown'}`, {
            name: item.name,
            role: item.tags?.['ai.cloud.role'],
            baseData: item.baseData,
            data: item.data,
        });
    });
}

/**
 * Whether the echo is switched on, keyed on the `pcp-log-level` **override alone**.
 *
 * Deliberately not the *effective* console floor. That floor is already `VERBOSE` in local
 * development, so falling back to it would leave the echo permanently on for every developer — and
 * this is a diagnostic you reach for, not a default. Reading only the override keeps it off
 * everywhere until someone asks for it, in production exactly as locally.
 *
 * Re-read per item rather than captured once, so it can be turned on in a tab that is already
 * running — within the accessor's 5s cache, which is what makes a per-item read affordable.
 */
function isEchoEnabled(): boolean {
    const override = getLocalStorageLogLevel(CONSOLE_LEVEL_KEY);
    return override !== null && override <= LogLevel.DEBUG;
}
